import { useEffect, useRef, useState } from "react";

import { Button, Stack, TextField, Typography } from "@mui/material";
import { isNil } from "lodash";

import { ErrorTypography } from "components/ErrorTypography";
import { useSwrFetch } from "hooks";

interface ExistsResponse {
  available: boolean;
}

interface Props {
  handleSearchCompanySelected: () => void;
  onCompanyDoesNotAlreadyExist: (companyInfo: { name: string }) => void;
  existingCompanyErrorMessage: string;
  checkFailedErrorMessage: string;
  companyNamePlaceholder: string;
}

export function CheckCompanyExists(props: Props) {
  const {
    handleSearchCompanySelected,
    onCompanyDoesNotAlreadyExist,
    existingCompanyErrorMessage,
    checkFailedErrorMessage,
    companyNamePlaceholder,
  } = props;

  const [companyName, setCompanyName] = useState<string>("");
  const [checkedCompanyName, setCheckedCompanyName] = useState<string | null>(
    null,
  );

  const {
    data: existsResponse,
    loading,
    validating,
    error: checkFailedError,
    refetch,
  } = useSwrFetch<ExistsResponse>({
    url: isNil(checkedCompanyName)
      ? null
      : `/companies/exists?${new URLSearchParams({
          name: checkedCompanyName,
        }).toString()}`,
    errorMessage: checkFailedErrorMessage,
  });

  const companyAlreadyExists =
    !isNil(existsResponse) && !existsResponse.available;
  const error =
    checkFailedError ??
    (companyAlreadyExists ? existingCompanyErrorMessage : null);

  const notifiedCompanyName = useRef<string | null>(null);

  useEffect(() => {
    if (isNil(checkedCompanyName) || existsResponse?.available !== true) {
      return;
    }
    if (notifiedCompanyName.current === checkedCompanyName) {
      return;
    }
    notifiedCompanyName.current = checkedCompanyName;
    onCompanyDoesNotAlreadyExist({ name: checkedCompanyName });
  }, [checkedCompanyName, existsResponse, onCompanyDoesNotAlreadyExist]);

  const handleCompanyNameChange = (updatedCompanyName: string): void => {
    setCompanyName(updatedCompanyName);
    setCheckedCompanyName(null);
  };

  const handleContinue = (): void => {
    if (checkedCompanyName === companyName) {
      refetch();
      return;
    }
    setCheckedCompanyName(companyName);
  };

  const isCheckingAvailability = loading || validating;

  return (
    <Stack spacing={3} direction="column">
      <Typography textAlign="center" variant="h4" fontWeight="bold">
        Create your company profile
      </Typography>
      <Typography variant="h6">Company Name</Typography>
      <TextField
        onChange={(e) => handleCompanyNameChange(e.target.value)}
        placeholder={companyNamePlaceholder}
        required
      />
      <Button
        variant="contained"
        disabled={companyName.length === 0 || isCheckingAvailability}
        onClick={handleContinue}
      >
        continue
      </Button>
      {!isNil(error) && <ErrorTypography error={error} />}
      <Stack justifyContent="center" spacing={2} alignItems="center">
        <Typography variant="body1">
          <b>Is your company already a part of Evotrux?</b>
        </Typography>
        <Button variant="contained" onClick={handleSearchCompanySelected}>
          Join your company
        </Button>
      </Stack>
    </Stack>
  );
}
